import {
  isUsableNeighborhood,
  lookupCanadianPostalCode,
  refineCanadianCivicLocation,
} from 'api/services/nominatimService'
import { splitCanadianUnitCivicAddress } from 'api/utils/canadianCivicAddress'
import classNames from 'classnames'
import OsmAddressSearchInput from 'components/common/field/OsmAddressSearchInput'
import OsmMapPreview from 'components/common/OsmMapPreview'
import {
  formatCanadianPostalCodeInput,
  hasGeocodedAddress,
  sanitizeCityInput,
  sanitizeNeighborhoodInput,
  sanitizeStreetInput,
  sanitizeUnitInput,
} from 'components/createAd/createAdAddressValidation'
import { CreateAdFlowForm, isValidCanadianPostalCode } from 'components/createAd/createAdFlowState'
import FloorSelect, { FloorCustomInput } from 'components/createAd/FloorSelect'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ParsedOsmAddress } from 'types/osmAddress'

interface CreateAdAddressSectionProps {
  form: CreateAdFlowForm
  update: <K extends keyof CreateAdFlowForm>(
    key: K,
    value: CreateAdFlowForm[K]
  ) => void
  showPostalError: boolean
  onPostalBlur: () => void
  showStreetError: boolean
  showUnitError: boolean
  showCityError: boolean
  showNeighborhoodError: boolean
  showGeocodeError: boolean
  onStreetBlur: () => void
  onUnitBlur: () => void
  onCityBlur: () => void
  onNeighborhoodBlur: () => void
}

const cityInputClass =
  'w-full rounded-xl border border-gray-200 bg-white py-3 px-4 focus:outline-none focus:ring-0 autofill:bg-white autofill:shadow-[inset_0_0_0px_1000px_#ffffff]'

const CreateAdAddressSection = ({
  form,
  update,
  showPostalError,
  onPostalBlur,
  showStreetError,
  showUnitError,
  showCityError,
  showNeighborhoodError,
  showGeocodeError,
  onStreetBlur,
  onUnitBlur,
  onCityBlur,
  onNeighborhoodBlur,
}: CreateAdAddressSectionProps) => {
  const { t } = useTranslation()
  const [cityFromAddress, setCityFromAddress] = useState(false)
  const [neighborhoodFromLookup, setNeighborhoodFromLookup] = useState(false)
  const [isPostalLookupLoading, setIsPostalLookupLoading] = useState(false)
  const postalLookupRequestRef = useRef(0)

  const applyAddressLookup = useCallback(
    (address: ParsedOsmAddress, options?: { preserveCity?: boolean }) => {
      if (address.city && !options?.preserveCity) {
        update('city', address.city)
        setCityFromAddress(true)
      } else if (address.city && !form.city.trim()) {
        update('city', address.city)
        setCityFromAddress(true)
      }

      if (isUsableNeighborhood(address.neighborhood, address.city)) {
        update('neighborhood', address.neighborhood)
        setNeighborhoodFromLookup(true)
      } else if (!options?.preserveCity) {
        // Drop Photon-style "Lévis (quartier)" so the user can type the real borough.
        update('neighborhood', '')
        setNeighborhoodFromLookup(false)
      }

      if (address.province) update('province', address.province)
      if (address.country) update('country', address.country)
      update('latitude', address.location.lat)
      update('longitude', address.location.lng)
    },
    [form.city, update]
  )

  const handleAddressSelect = useCallback(
    (address: ParsedOsmAddress) => {
      const typedUnit = splitCanadianUnitCivicAddress(form.street).unit
      const unit = sanitizeUnitInput(address.unit || typedUnit || '')
      const streetLine = sanitizeStreetInput(address.street || '')

      // Apply civic street + apartment immediately (map refine is async).
      if (streetLine) update('street', streetLine)
      if (unit) update('unit', unit)

      void (async () => {
        const refined = await refineCanadianCivicLocation({
          ...address,
          street: streetLine || address.street,
          unit: unit || address.unit,
        })
        update('street', sanitizeStreetInput(refined.street))
        const refinedUnit = sanitizeUnitInput(refined.unit || unit)
        if (refinedUnit) {
          update('unit', refinedUnit)
        }
        if (refined.postalCode) {
          update('postalCode', formatCanadianPostalCodeInput(refined.postalCode))
        }
        applyAddressLookup(refined)
      })()
    },
    [applyAddressLookup, form.street, update]
  )

  useEffect(() => {
    if (!isValidCanadianPostalCode(form.postalCode)) {
      setIsPostalLookupLoading(false)
      return
    }

    if (hasGeocodedAddress(form)) {
      setIsPostalLookupLoading(false)
      return
    }

    const requestId = ++postalLookupRequestRef.current
    setIsPostalLookupLoading(true)

    const timer = window.setTimeout(() => {
      void lookupCanadianPostalCode(form.postalCode)
        .then(result => {
          if (requestId !== postalLookupRequestRef.current) return
          if (!result) return

          applyAddressLookup(result, { preserveCity: Boolean(form.city.trim()) })
        })
        .finally(() => {
          if (requestId === postalLookupRequestRef.current) {
            setIsPostalLookupLoading(false)
          }
        })
    }, 500)

    return () => {
      window.clearTimeout(timer)
    }
  }, [applyAddressLookup, form.city, form.latitude, form.longitude, form.postalCode])

  const hasMapPreview =
    form.latitude != null &&
    form.longitude != null &&
    Number.isFinite(form.latitude) &&
    Number.isFinite(form.longitude)

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="relative z-20 sm:col-span-2">
          <OsmAddressSearchInput
            id="create-ad-street"
            label={t('createAd.flow.fields.street')}
            placeholder={t('createAd.flow.addressSearch.streetPlaceholder')}
            value={form.street}
            maxLength={120}
            showError={showStreetError}
            errorMessage={t('createAd.flow.validation.invalidStreet')}
            onValueChange={value => {
              update('street', sanitizeStreetInput(value))
              if (form.latitude != null || form.longitude != null) {
                update('latitude', null)
                update('longitude', null)
              }
            }}
            onBlur={onStreetBlur}
            onAddressSelect={handleAddressSelect}
          />
        </div>

        {hasMapPreview ? (
          <div className="relative z-0 sm:col-span-2">
            <p className="mb-2 text-xs font-medium text-gray-600">
              {t('createAd.flow.addressSearch.mapPreview')}
            </p>
            <OsmMapPreview
              location={{ lat: form.latitude!, lng: form.longitude! }}
              height="220px"
            />
          </div>
        ) : null}

        {showGeocodeError ? (
          <p className="sm:col-span-2 text-xs leading-snug text-red-600" role="alert">
            {t('createAd.flow.validation.addressGeocodeRequired')}
          </p>
        ) : null}

        <div>
          <label className="mb-1.5 block text-sm font-semibold text-gray-800">
            {t('createAd.flow.fields.unit')}
          </label>
          <input
            placeholder={t('createAd.flow.placeholders.unit')}
            value={form.unit}
            maxLength={15}
            onChange={e => update('unit', sanitizeUnitInput(e.target.value))}
            onBlur={onUnitBlur}
            aria-invalid={showUnitError}
            className={classNames(
              'w-full rounded-xl border py-3 px-4 focus:outline-none focus:ring-0',
              showUnitError ? 'border-red-500' : 'border-gray-200'
            )}
          />
          {showUnitError ? (
            <p className="mt-1.5 text-xs leading-snug text-red-600" role="alert">
              {t('createAd.flow.validation.invalidUnit')}
            </p>
          ) : null}
        </div>
        <FloorSelect
          idPrefix="create-ad-address-floor"
          floor={form.floor}
          floorCustom={form.floorCustom}
          hideCustom
          onFloorChange={value => update('floor', value)}
          onFloorCustomChange={value => update('floorCustom', value)}
        />
        {form.floor === '5_plus' ? (
          <FloorCustomInput
            idPrefix="create-ad-address-floor"
            floorCustom={form.floorCustom}
            onFloorCustomChange={value => update('floorCustom', value)}
          />
        ) : null}
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-gray-800">
            {t('createAd.flow.fields.city')}
          </label>
          <input
            autoComplete="off"
            placeholder={t('createAd.flow.placeholders.city')}
            value={form.city}
            maxLength={40}
            onChange={e => {
              update('city', sanitizeCityInput(e.target.value))
              setCityFromAddress(false)
            }}
            onBlur={onCityBlur}
            aria-invalid={showCityError}
            className={classNames(cityInputClass, showCityError ? 'border-red-500' : '')}
          />
          {showCityError ? (
            <p className="mt-1.5 text-xs leading-snug text-red-600" role="alert">
              {t('createAd.flow.validation.invalidCity')}
            </p>
          ) : cityFromAddress ? (
            <p className="mt-1.5 text-xs leading-snug text-gray-500">
              {t('createAd.flow.addressSearch.cityAutoFilled')}
            </p>
          ) : null}
        </div>
        <div>
          <label
            htmlFor="create-ad-postal-code"
            className="mb-1.5 block text-sm font-semibold text-gray-800"
          >
            {t('createAd.flow.fields.postal')}
          </label>
          <input
            id="create-ad-postal-code"
            placeholder={t('createAd.flow.placeholders.postal')}
            value={form.postalCode}
            maxLength={7}
            onChange={e => update('postalCode', formatCanadianPostalCodeInput(e.target.value))}
            onBlur={onPostalBlur}
            aria-invalid={showPostalError}
            aria-describedby={showPostalError ? 'create-ad-postal-code-error' : undefined}
            className={classNames(
              'w-full rounded-xl border py-3 px-4 uppercase focus:outline-none focus:ring-0',
              showPostalError ? 'border-red-500' : 'border-gray-200'
            )}
          />
          {showPostalError ? (
            <p
              id="create-ad-postal-code-error"
              role="alert"
              className="mt-1.5 text-xs leading-snug text-red-600"
            >
              {t('createAd.flow.validation.invalidPostalCode')}
            </p>
          ) : null}
          {isPostalLookupLoading ? (
            <p className="mt-1.5 text-xs leading-snug text-gray-500">
              {t('createAd.flow.addressSearch.postalLookupLoading')}
            </p>
          ) : null}
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-gray-800">
            {t('createAd.flow.fields.neighborhood')}
          </label>
          <input
            placeholder={t('createAd.flow.placeholders.neighborhood')}
            value={form.neighborhood}
            maxLength={60}
            onChange={e => {
              update('neighborhood', sanitizeNeighborhoodInput(e.target.value))
              setNeighborhoodFromLookup(false)
            }}
            onBlur={onNeighborhoodBlur}
            aria-invalid={showNeighborhoodError}
            className={classNames(
              'w-full rounded-xl border py-3 px-4 focus:outline-none focus:ring-0',
              showNeighborhoodError ? 'border-red-500' : 'border-gray-200'
            )}
          />
          {showNeighborhoodError ? (
            <p className="mt-1.5 text-xs leading-snug text-red-600" role="alert">
              {t('createAd.flow.validation.invalidNeighborhood')}
            </p>
          ) : neighborhoodFromLookup ? (
            <p className="mt-1.5 text-xs leading-snug text-gray-500">
              {t('createAd.flow.addressSearch.neighborhoodAutoFilled')}
            </p>
          ) : null}
        </div>
      </div>
      <p className="text-xs leading-snug text-gray-500">
        {t('createAd.flow.addressSearch.privacyNote')}
      </p>
    </div>
  )
}

export default CreateAdAddressSection
